import { useQuery } from '@tanstack/react-query';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { StatCard } from '../../components/ui/DefinitionList';
import { Spinner } from '../../components/ui/Spinner';
import { StatusText } from '../../components/ui/StatusText';
import { formatCount, formatDayTime } from '../../lib/format';
import { useI18nTranslation } from '../../lib/use-i18n-translation';

const HEALTH_REFETCH_MS = 30_000;

export const DashboardPage = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();

  const health = useQuery({
    ...orpc.health.get.queryOptions(),
    refetchInterval: HEALTH_REFETCH_MS,
  });

  const stats = useQuery(orpc.benchmarks.dashboardStats.queryOptions());

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">{t('dashboard.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          isLoading={health.isFetching || stats.isFetching}
          onClick={() => {
            void health.refetch();
            void stats.refetch();
          }}
        >
          {t('actions.retry')}
        </Button>
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-medium text-foreground">{t('dashboard.catalogue')}</h2>

        {stats.isError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {t('dashboard.statsError')}
          </p>
        ) : stats.isPending ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner label={t('app.loading')} />
            {t('app.loading')}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label={t('dashboard.activeVehicles')}
              value={formatCount(stats.data.vehicles.active)}
              hint={t('dashboard.activeVehiclesHint', { archived: formatCount(stats.data.vehicles.archived) })}
            />
            <StatCard
              label={t('dashboard.scenarios')}
              value={formatCount(stats.data.scenarios.total)}
              hint={t('dashboard.scenariosHint', {
                core: formatCount(stats.data.scenarios.core),
                active: formatCount(stats.data.scenarios.active),
              })}
            />
            <StatCard
              label={t('dashboard.routes')}
              value={formatCount(stats.data.routes.active)}
              hint={t('dashboard.routesHint', { superseded: formatCount(stats.data.routes.superseded) })}
            />
            <StatCard
              label={t('dashboard.segments')}
              value={formatCount(stats.data.segments.active)}
            />
            <StatCard
              label={t('dashboard.protocols')}
              value={formatCount(stats.data.protocols.active)}
            />
            <StatCard
              label={t('dashboard.testRuns')}
              value={formatCount(stats.data.runs.total)}
              hint={t('dashboard.testRunsHint', {
                valid: formatCount(stats.data.runs.valid + stats.data.runs.validWithWarning),
                invalid: formatCount(stats.data.runs.invalid),
                drafts: formatCount(stats.data.runs.drafts),
              })}
            />
          </div>
        )}
      </section>

      <div className="grid gap-4 sm:grid-cols-2">
        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-medium text-card-foreground">{t('dashboard.serviceHealth')}</h2>

          {health.isPending ? (
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner label={t('app.loading')} />
              {t('app.loading')}
            </div>
          ) : health.isError ? (
            <p className="mt-4 text-sm text-destructive">{t('dashboard.healthError')}</p>
          ) : (
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{t('dashboard.apiStatus')}</dt>
                <dd>
                  <StatusText
                    label={health.data.status.toUpperCase()}
                    tone="success"
                  />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{t('dashboard.database')}</dt>
                <dd>
                  <StatusText
                    label={health.data.db ? t('dashboard.databaseConnected') : t('dashboard.databaseUnreachable')}
                    tone={health.data.db ? 'success' : 'danger'}
                  />
                </dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{t('dashboard.lastChecked')}</dt>
                <dd className="text-foreground">{formatDayTime(health.data.time)}</dd>
              </div>
            </dl>
          )}
        </section>

        <section className="rounded-lg border border-border bg-card p-5">
          <h2 className="text-sm font-medium text-card-foreground">{t('dashboard.signedInAs')}</h2>

          {user ? (
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{t('dashboard.signedInAs')}</dt>
                <dd className="truncate text-foreground">{user.name}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{t('dashboard.email')}</dt>
                <dd className="truncate font-mono text-xs text-muted-foreground">{user.email}</dd>
              </div>
              <div className="flex items-center justify-between gap-4">
                <dt className="text-muted-foreground">{t('dashboard.role')}</dt>
                <dd>
                  <StatusText
                    label={t(`roles.${user.role}`)}
                    tone={user.role === 'MANAGER' ? 'info' : 'muted'}
                  />
                </dd>
              </div>
            </dl>
          ) : null}
        </section>
      </div>
    </div>
  );
};
